import { useMutation } from '@apollo/client';
import { UpdateModulesDocument } from '@risksmart-app/web-graphql-client/generated/graphql';

import { useIsFeatureFlagEnabled } from '@/hooks/useIsFeatureFlagEnabled';
import { evictField } from '@/utils/graphqlUtils';

import {
  type UpdateModulesCompatibilityResult,
  type UpdateModulesTRPCInput,
  useUpdateModulesTRPC,
} from './useUpdateModulesTRPC';

type MutateArgs = { variables: UpdateModulesTRPCInput };

/**
 * Flag-gated wrapper for the `updateModules` mutation. Defaults to
 * the legacy Apollo mutation and delegates to the tRPC variant when
 * the `trpc` feature flag is enabled.
 *
 * Returns an Apollo-compatible tuple `[mutate, { loading, error }]`
 * so the existing `moduleContext` call site (which destructures the
 * tuple and invokes `mutate({ variables })`) keeps working unchanged.
 */
export const useUpdateModules = () => {
  const trpcEnabled = useIsFeatureFlagEnabled('trpc');

  const [updateModulesGraphQL, graphqlState] = useMutation(
    UpdateModulesDocument,
    {
      update: (cache) => {
        evictField(cache, 'organisation_module');
      },
    }
  );

  const trpcMutation = useUpdateModulesTRPC();

  const mutate = async ({
    variables,
  }: MutateArgs): Promise<UpdateModulesCompatibilityResult> => {
    if (trpcEnabled) {
      return trpcMutation.updateModules({
        ModuleSettings: variables.ModuleSettings,
      });
    }

    const result = await updateModulesGraphQL({ variables });
    if (!result.data) {
      throw new Error('Failed to update modules');
    }

    return result.data;
  };

  const state = trpcEnabled
    ? { loading: trpcMutation.loading, error: trpcMutation.error }
    : { loading: graphqlState.loading, error: graphqlState.error };

  return [mutate, state] as const;
};
